import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import ProductDetails from "@saleor/createOrder/components/ProductDetails";
import EmptyProduct from "@saleor/createOrder/components/ProductDetails/EmptyProduct";
import {
  ContractOrderLineUpdateInput,
  ContractOrderQuery,
  ContractOrderUpdateInput,
  OrderInformationInput,
  OrderOrganizationalDataInput,
  ScgOrderStatus,
  useCartTotalCheckoutQuery,
  useContractOrderQuery,
  useUpdateContractOrderMutation
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

// import CreateOrderFooterEditMode from "../../changeOrder/components/CreateOrderFooterEditMode";
import CreateOrderFooter from "../components/CreateOrderFooter";
import SaleOrgDetails from "../components/SaleOrgDetails";
import { contractUrl, createOrderUrl, orderProcessing } from "../urls";
import CustomerInfo from "./../components/CollectionForm";
import OrderProcessing from "./ModalOrderProcessing";

interface CreateOrderViewProps {
  id: string;
}

export interface ValidateFormProps {
  isCheckValidate: boolean;
  isValidate: {
    validateInfo: boolean;
    validateProduct: boolean;
    validateSale: boolean;
  };
}

export interface CustomerDataProps {
  customerInfo: ContractOrderQuery["contractOrder"]["customer"];
  poDate: any | null;
  poNumber: string | null;
  shipTo: string | null;
  billTo: string | null;
  requestDate: any | null;
  internalCommentsToWarehouse: string | null;
  internalCommentsToLogistic: string | null;
  externalCommentsToCustomer: string | null;
  productInformation: string | null;
  orderType: string | null;
  orderStatus: string | null;
}

let timer: NodeJS.Timer | null = null;
let timer1: NodeJS.Timer | null = null;
const CreateOrderView: React.FC<CreateOrderViewProps> = ({ id }) => {
  const intl = useIntl();
  const [listLineState, setListLineState] = useState([]);
  const [dataFormCustomer, setDataFormCustomer] = useState<any>({});
  const [dataOrganizationInfo, setDataOrganizationInfo] = useState({});
  const [visible, setVisible] = useState<boolean>(false);
  const [componentKey, setComponentKey] = useState<number>(0);
  const [, dispatchAppState] = useAppState();
  const navigate = useNavigator();

  const [validateForm, setValidateForm] = useState<ValidateFormProps>({
    isCheckValidate: false,
    isValidate: {
      validateInfo: false,
      validateProduct: false,
      validateSale: false
    }
  });
  const { data: dataOrder, refetch } = useContractOrderQuery({
    variables: {
      soNo: id
    },
    skip: !id
  });

  const {
    data: cartInfoData,
    refetch: refetchCart
  } = useCartTotalCheckoutQuery({
    skip: true
  });

  useEffect(() => {
    if (dataOrder) {
      const listLine = dataOrder.contractOrder.orderLines.map(item => ({
        ...item,
        errorQuantity: false,
        plant: item?.plant || "",
        internalCommentsToWarehouse: item?.internalCommentsToWarehouse || "",
        productInformation: item?.productInformation || "",
        requestDate: item?.requestDate || "",
        shipTo: item?.shipTo || ""
      }));
      setListLineState(listLine);
    }
  }, [dataOrder]);

  useEffect(() => {
    clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (cartInfoData) {
      const orderCount =
        cartInfoData.totalContractCheckouts?.totalProducts || 0;
      dispatchAppState({
        payload: {
          orderCount
        },
        type: "setOrderCount"
      });
    }
  }, [cartInfoData]);

  const reFetchData = () => {
    refetch();
  };

  const [
    handleSubmitOrder,
    { loading: loadingUpdateContractOrder }
  ] = useUpdateContractOrderMutation({
    onCompleted: () => {
      refetchCart();
      timer = setTimeout(() => {
        setVisible(false);
      }, 2000);
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    },
    onError: () => {
      timer = setTimeout(() => {
        setVisible(false);
      }, 2000);
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    }
  });

  const getTotal = () => {
    let total = 0;
    listLineState.forEach(item => {
      const totalOfLine = item.quantity * (item.contractProduct?.price || 0);
      total += totalOfLine;
    });
    return total;
  };

  const submitFormCustomer = data => {
    const _listLine = [...listLineState];
    _listLine.forEach(item => {
      item.shipTo = data.shipTo;
      item.requestDate = data.requestDate;
      item.internalCommentsToWarehouse = data.internalCommentsToWarehouse
        ? data.internalCommentsToWarehouse
        : item.internalCommentsToWarehouse;
      item.productInformation = data.productInformation
        ? data.productInformation
        : item.productInformation;
    });

    setListLineState(_listLine);
    setDataFormCustomer(data);
    setComponentKey(componentKey + 1);
  };

  const getDataSubmit = () => ({
    customer: dataFormCustomer,
    lines: listLineState
  });

  // Auto save draft order every 5 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      handleCreateOrder(ScgOrderStatus.DRAFT);
    }, 1000 * 60 * 5);
    return () => clearInterval(intervalId);
  }, []);

  const handleCreateOrder = async (submitType: ScgOrderStatus) => {
    const getLines: ContractOrderLineUpdateInput[] = listLineState.map(
      line => ({
        checkoutLineId: line?.checkoutLineId,
        quantity: line?.quantity,
        requestDate: line?.requestDate || undefined,
        plant: line?.plant,
        shipTo: line?.shipTo,
        internalCommentsToWarehouse: line?.internalCommentsToWarehouse,
        productInformation: line?.productInformation,
        variantId: line?.variant?.id,
        id: line?.id
      })
    );

    const params: ContractOrderUpdateInput = {
      orderInformation: {
        ...dataFormCustomer,
        customerId: dataOrder?.contractOrder?.customer?.id,
        poDate: dataFormCustomer.poDate
          ? moment(dataFormCustomer.poDate).format("YYYY-MM-DD")
          : null,
        requestDate: dataFormCustomer.requestDate
          ? moment(dataFormCustomer.requestDate).format("YYYY-MM-DD")
          : null
      } as OrderInformationInput,
      lines: getLines,
      orderOrganizationData: dataOrganizationInfo as OrderOrganizationalDataInput,
      status: submitType
    };
    if (submitType === ScgOrderStatus.CONFIRMED) {
      await handleCheckValidateForm();

      const checkValidateForm = Object.keys(validateForm.isValidate)
        .map(key => validateForm.isValidate[key])
        .filter(ck => ck);
      // Pass isValidate[key] === true
      if (checkValidateForm.length === 3) {
        handleSubmitOrder({ variables: { id, input: params } });
        // Navigate to new page
        navigate(orderProcessing);
      }
    } else {
      handleSubmitOrder({ variables: { id, input: params } });
      dispatchAppState({
        payload: {
          value: true
        },
        type: "displayLoader"
      });
    }
  };

  const handleGetOrganizationInfo = values => {
    setDataOrganizationInfo(values);
  };

  const handleGetCustomerInfo = values => {
    setDataFormCustomer(values);
  };
  const customerInfoData = useMemo(() => {
    const customerData: CustomerDataProps = {
      customerInfo: dataOrder?.contractOrder?.customer,
      billTo: dataOrder?.contractOrder?.billTo,
      shipTo: dataOrder?.contractOrder?.shipTo,
      poDate: dataOrder?.contractOrder?.poDate,
      poNumber: dataOrder?.contractOrder?.poNumber,
      externalCommentsToCustomer:
        dataOrder?.contractOrder?.externalCommentsToCustomer,
      internalCommentsToLogistic:
        dataOrder?.contractOrder?.internalCommentsToLogistic,
      internalCommentsToWarehouse:
        dataOrder?.contractOrder?.internalCommentsToWarehouse,
      productInformation: dataOrder?.contractOrder?.productInformation,
      requestDate: dataOrder?.contractOrder?.requestDate || undefined,
      orderType: dataOrder?.contractOrder?.orderType,
      orderStatus: dataOrder?.contractOrder?.status
    };
    return customerData;
  }, [dataOrder?.contractOrder]);

  const hasValidationError = useMemo(() => {
    // Validate requestDate value should in furture
    const requestDateValue = dataFormCustomer?.requestDate;
    const checkRequestDateValueinLines = listLineState.filter(
      ck =>
        ck.requestDate &&
        new Date(ck.requestDate).getTime() <= new Date().getTime()
    );

    return (
      listLineState?.length < 1 ||
      checkRequestDateValueinLines.length > 0 ||
      (requestDateValue &&
        new Date(requestDateValue).getTime() <= new Date().getTime())
    );
  }, [listLineState, dataFormCustomer?.requestDate]);

  const handleBack = () => {
    if (dataOrder?.contractOrder?.customer?.id) {
      navigate(contractUrl(dataOrder.contractOrder.customer.id));
      return;
    }
    navigate(createOrderUrl);
  };

  const handleCheckValidateForm = () => {
    setValidateForm({ ...validateForm, isCheckValidate: true });
  };

  useEffect(() => {
    if (validateForm.isCheckValidate) {
      timer1 = setTimeout(
        () => setValidateForm({ ...validateForm, isCheckValidate: false }),
        200
      );

      return () => clearTimeout(timer1);
    }
  }, [validateForm.isCheckValidate]);

  const getErrorLine = () => listLineState.some(item => item.errorQuantity);

  return (
    <>
      <Container>
        <PageHeader
          title={intl.formatMessage({ defaultMessage: "สร้างคำสั่งซื้อ" })}
        />
        {customerInfoData?.customerInfo && (
          <CustomerInfo
            customerData={customerInfoData}
            submitForm={submitFormCustomer}
            handleGetCustomerInfo={handleGetCustomerInfo}
            validateForm={validateForm}
            setValidateForm={setValidateForm}
          />
        )}
        {dataOrder && listLineState.length ? (
          <ProductDetails
            key={componentKey}
            refetch={reFetchData}
            dataOrder={dataOrder}
            setListLine={setListLineState}
            listLine={listLineState}
            validateForm={validateForm}
            setValidateForm={setValidateForm}
          />
        ) : (
          <EmptyProduct />
        )}
        <SaleOrgDetails
          handleOrganizationInfo={handleGetOrganizationInfo}
          dataOrder={dataOrder?.contractOrder}
          validateForm={validateForm}
          setValidateForm={setValidateForm}
        />
        <CreateOrderFooter
          onOrder={handleCreateOrder}
          total={getTotal()}
          dataSubmit={getDataSubmit()}
          disabled={
            (customerInfoData?.orderStatus || "").toUpperCase() ===
              ScgOrderStatus.CONFIRMED || loadingUpdateContractOrder
          }
          hasValidationError={hasValidationError || getErrorLine()}
          onBack={handleBack}
        />
      </Container>
      {visible && <OrderProcessing visible />}
    </>
  );
};

export default CreateOrderView;
